import { StatsWrapper } from "./style";
import { Product } from "./product";
import { LineChart } from "./LIneChart";
import useQuery from "@/hooks/useQuery";
import { DASHBOARD_STATS_URL } from "@/common/constants";
import { PageSkeletonLoader } from "@/common/components/skeleton/page-skeleton";
import { formatKey } from "@/common/utils/general";
import { DoughnutChart } from "@/common/components/charts/donut-charts";
import { KeyValueObj } from "@/common/types";
import { LargeCard } from "./LargeCard";
import {
  boxIcon,
  deliveryIcon,
  greenBoxIcon,
} from "@/common/components/icons/svg-icons";
import RegistrationBanner from "@/common/components/banner";
import { PCNRegStatus } from "@/types/enum";

const deliveryColors = {
  warehouse_sorting: "#5d27da",
  shipped: "#3689cd",
  pending: "#ECAF57",
  delivered: "#36CDB0",
};

export interface IStatsProps {}

export function DistributorStats(_: IStatsProps) {
  const { data, loading } = useQuery(DASHBOARD_STATS_URL);
  const deliveryStatus = data?.inbound_orders?.by_delivery_status || {};
  const productShipped =
    data?.product_metrics?.shipped_orders?.shipped_outbound_orders || 0;
  const totalOrderRecieved = data?.inbound_orders?.total_inbound || 0;
  const orderStatus = data?.outbound_orders?.by_order_status || {};

  let PCN_BASE_URL = import.meta.env.VITE_PCN_FRONTEND_BASE_URL;
  const url = PCN_BASE_URL + "self-service/auth/register";

  function handleNavigateToPCN() {
    window.open(url, "_blank")?.focus();
  }

  const isOrderStatusEmpty = (status: Record<string, number>): boolean => {
    return Object.values(status).reduce((sum, value) => sum + value, 0) === 0;
  };

  const isEmpty = isOrderStatusEmpty(orderStatus);

  const orderCategories = Object.keys(orderStatus || {});
  const orderSeries = Object.values(orderStatus || {});

  const retailers = data?.active_retailers?.map((retailers: KeyValueObj) => ({
    name: retailers?.name,
    orderCount: retailers?.order_count,
  }));

  if (loading) return <PageSkeletonLoader />;

  const regStatus = sessionStorage.getItem("regStatus");

  return (
    <StatsWrapper>
      {regStatus !== PCNRegStatus.COMPLETED && (
        <>
          <RegistrationBanner onCompleteClick={handleNavigateToPCN} />
        </>
      )}
      <div className="total-cards">
        <LargeCard
          icon={boxIcon}
          description="Products available"
          value={data?.product_metrics?.total_products_in_inventory || 0}
          title="Total Products"
          color="#E43754"
        />

        <LargeCard
          icon={deliveryIcon}
          description={`${productShipped} shipped in the last month`}
          value={productShipped || 0}
          title="Total Products Shipped"
          color="#F69913"
        />

        <LargeCard
          icon={greenBoxIcon}
          value={totalOrderRecieved || 0}
          description={`${totalOrderRecieved} orders in the last month`}
          title="Total Orders Received"
          color="#015A49"
        />
      </div>

      <div className="product-stat-wrapper">
        <h2 className="delivery-title">Delivery Status</h2>

        <div className="product-cards">
          {Object.keys(deliveryStatus)?.map((key: string, i: number) => (
            <Product
              key={i}
              amount={deliveryStatus?.[key] || 0}
              title={formatKey(key)}
              increase=""
              bgColor={deliveryColors?.[key as keyof typeof deliveryColors]}
            />
          ))}
        </div>
      </div>

      <div className="top-row flex-row">
        <div className="client-chart">
          {isEmpty ? (
            <p className="text-red-500 font-medium">No orders available</p>
          ) : (
            <DoughnutChart
              categories={orderCategories}
              series={orderSeries}
              title="Order Status"
            />
          )}
        </div>

        <div className="distributors top-card">
          <h2 className="dash-title underline">Active Retailers</h2>
          <div className="distri-table flex-col">
            <div className="distri-tb-row flex-row">
              <h3 className="table-header left">Company Name</h3>
              <h3 className="table-header right">Order Count</h3>
            </div>

            {retailers?.map((distributor: KeyValueObj, i: number) => (
              <div key={i} className="distri-tb-row flex-row">
                <h3 className="table-text left">{distributor?.name}</h3>
                <h3 className="table-text right">{distributor?.orderCount}</h3>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="lower-row flex-row">
        <div className="line-chart">
          <LineChart data={data?.inventory_metrics} />
        </div>
        <div className="notifications flex-col">
          <div className="title flex-row">
            <h2 className="dash-title">Notifications</h2>
            <h3>View All</h3>
          </div>
          <div className="notifications-wrapper flex-col">
            <p className="no-notif">No notifications at the moment</p>
          </div>
        </div>
      </div>
    </StatsWrapper>
  );
}
